import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  type CollectionReference,
  type DocumentData,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import type {
  CreateHostInput,
  CreateSoftwareInstanceInput,
  ICenterInfra,
  IContact,
  ICustomField,
  IDicomEndpoint,
  IHost,
  IHostVerificationRow,
  IInfraAddress,
  ISoftwareInstance,
  ISoftwareInventoryRow,
  ISoftwareType,
  UpdateHostInput,
  UpdateSoftwareInstanceInput,
  UpsertCenterInfraInput,
} from "@/features/servers/schemas/servers";
import { DEFAULT_OS_SUPPORT_POLICY } from "@/features/servers/schemas/servers";
import { getInfraSettings } from "@/features/servers/services/infraSettingsService";
import { listSoftwareTypes } from "@/features/servers/services/softwareTypesService";
import {
  acceptsSoftwareUpdates,
  evaluateOsSupport,
  resolveHostOsVersion,
} from "@/features/servers/utils/os-support";
import { getUserById } from "@/features/users/services/users";
import { getUserFullName } from "@/features/users/utils/user-utils";

export const CENTER_INFRA_COLLECTION = "center_infra";
export const HOSTS_SUBCOLLECTION = "hosts";
export const SOFTWARE_INSTANCES_SUBCOLLECTION = "software_instances";

function centerRef(institutionUid: string) {
  return doc(db, CENTER_INFRA_COLLECTION, institutionUid);
}

function hostsCollection(institutionUid: string): CollectionReference<DocumentData> {
  return collection(
    db,
    CENTER_INFRA_COLLECTION,
    institutionUid,
    HOSTS_SUBCOLLECTION
  );
}

function instancesCollection(
  institutionUid: string
): CollectionReference<DocumentData> {
  return collection(
    db,
    CENTER_INFRA_COLLECTION,
    institutionUid,
    SOFTWARE_INSTANCES_SUBCOLLECTION
  );
}

function mapCustomFields(value: unknown): ICustomField[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (item): item is { key: string; value: string } =>
        typeof item === "object" &&
        item !== null &&
        typeof (item as { key?: unknown }).key === "string"
    )
    .map((item) => ({
      key: item.key,
      value: typeof item.value === "string" ? item.value : String(item.value ?? ""),
    }));
}

function mapContacts(value: unknown): IContact[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null && typeof item.name === "string"
    )
    .map((item) => ({
      name: String(item.name),
      role: typeof item.role === "string" ? item.role : undefined,
      phone: typeof item.phone === "string" ? item.phone : undefined,
      email: typeof item.email === "string" ? item.email : undefined,
    }));
}

function mapDicomEndpoints(value: unknown): IDicomEndpoint[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" &&
        item !== null &&
        typeof item.aetitle === "string" &&
        (typeof item.port === "number" || typeof item.port === "string")
    )
    .map((item) => ({
      aetitle: String(item.aetitle),
      port: Number(item.port),
      ip: typeof item.ip === "string" ? item.ip : undefined,
      label: typeof item.label === "string" ? item.label : undefined,
    }));
}

function mapInfraAddress(value: unknown): IInfraAddress | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const o = value as Record<string, unknown>;
  const address: IInfraAddress = {
    street: typeof o.street === "string" ? o.street : undefined,
    city: typeof o.city === "string" ? o.city : undefined,
    state: typeof o.state === "string" ? o.state : undefined,
    country: typeof o.country === "string" ? o.country : undefined,
    zip: typeof o.zip === "string" ? o.zip : undefined,
  };
  const hasAny = Object.values(address).some((v) => !!v?.trim());
  return hasAny ? address : undefined;
}

function mapHost(docId: string, data: DocumentData): IHost {
  const kind =
    data.kind === "vm" || data.kind === "station" || data.kind === "physical"
      ? data.kind
      : "physical";

  const osFamily =
    data.osFamily === "ubuntu" ||
    data.osFamily === "windows" ||
    data.osFamily === "other"
      ? data.osFamily
      : undefined;

  return {
    id: docId,
    name: typeof data.name === "string" ? data.name : "Sin nombre",
    kind,
    ip: typeof data.ip === "string" ? data.ip : undefined,
    anydeskId: typeof data.anydeskId === "string" ? data.anydeskId : undefined,
    anydeskPass: typeof data.anydeskPass === "string" ? data.anydeskPass : undefined,
    os: typeof data.os === "string" ? data.os : undefined,
    osFamily,
    osVersion: typeof data.osVersion === "string" ? data.osVersion : undefined,
    osUser: typeof data.osUser === "string" ? data.osUser : undefined,
    osPass: typeof data.osPass === "string" ? data.osPass : undefined,
    hostname: typeof data.hostname === "string" ? data.hostname : undefined,
    hardware: typeof data.hardware === "string" ? data.hardware : undefined,
    verifiedAt: typeof data.verifiedAt === "string" ? data.verifiedAt : undefined,
    verifiedBy: typeof data.verifiedBy === "string" ? data.verifiedBy : undefined,
    customFields: mapCustomFields(data.customFields),
  };
}

function mapInstance(docId: string, data: DocumentData): ISoftwareInstance {
  return {
    id: docId,
    softwareTypeId:
      typeof data.softwareTypeId === "string" ? data.softwareTypeId : "",
    hostId: typeof data.hostId === "string" ? data.hostId : undefined,
    active: data.active !== false,
    mail: typeof data.mail === "string" ? data.mail : undefined,
    password: typeof data.password === "string" ? data.password : undefined,
    url: typeof data.url === "string" ? data.url : undefined,
    version: typeof data.version === "string" ? data.version : undefined,
    lastUpdatedAt:
      typeof data.lastUpdatedAt === "string" ? data.lastUpdatedAt : undefined,
    verifiedAt: typeof data.verifiedAt === "string" ? data.verifiedAt : undefined,
    verifiedBy: typeof data.verifiedBy === "string" ? data.verifiedBy : undefined,
    dicomEndpoints: mapDicomEndpoints(data.dicomEndpoints),
    customFields: mapCustomFields(data.customFields),
  };
}

function emptyInfra(institutionUid: string): ICenterInfra {
  return {
    institutionUid,
    contacts: [],
    publicIps: [],
    customFields: [],
  };
}

/**
 * Obtiene la cabecera de infra. Si no existe, retorna estructura vacía (lazy create).
 */
export async function getCenterInfra(
  institutionUid: string
): Promise<ICenterInfra> {
  const snap = await getDoc(centerRef(institutionUid));
  if (!snap.exists()) return emptyInfra(institutionUid);

  const data = snap.data();
  return {
    institutionUid,
    address: mapInfraAddress(data.address),
    contacts: mapContacts(data.contacts),
    publicIps: Array.isArray(data.publicIps)
      ? data.publicIps.filter((ip): ip is string => typeof ip === "string")
      : [],
    customFields: mapCustomFields(data.customFields),
    createdAt: typeof data.createdAt === "string" ? data.createdAt : undefined,
    updatedAt: typeof data.updatedAt === "string" ? data.updatedAt : undefined,
  };
}

/**
 * Crea o actualiza la cabecera. Lazy create en el primer guardado.
 */
export async function upsertCenterInfra(
  institutionUid: string,
  input: UpsertCenterInfraInput
): Promise<ICenterInfra> {
  const now = new Date().toISOString();
  const ref = centerRef(institutionUid);
  const existing = await getDoc(ref);

  const payload: Record<string, unknown> = {
    institutionUid,
    updatedAt: now,
  };

  if (input.address !== undefined) payload.address = input.address;
  if (input.contacts !== undefined) payload.contacts = input.contacts;
  if (input.publicIps !== undefined) payload.publicIps = input.publicIps;
  if (input.customFields !== undefined) payload.customFields = input.customFields;

  if (!existing.exists()) {
    payload.createdAt = now;
    if (input.contacts === undefined) payload.contacts = [];
    if (input.publicIps === undefined) payload.publicIps = [];
    if (input.customFields === undefined) payload.customFields = [];
    await setDoc(ref, payload);
  } else {
    await updateDoc(ref, payload);
  }

  return getCenterInfra(institutionUid);
}

/**
 * Indica qué institutionUids de la lista ya tienen documento de infra.
 */
export async function listCenterInfraExistence(
  uids: string[]
): Promise<Set<string>> {
  const found = new Set<string>();
  await Promise.all(
    uids.map(async (uid) => {
      const snap = await getDoc(centerRef(uid));
      if (snap.exists()) found.add(uid);
    })
  );
  return found;
}

export async function listHosts(institutionUid: string): Promise<IHost[]> {
  const snapshot = await getDocs(hostsCollection(institutionUid));
  return snapshot.docs
    .map((d) => mapHost(d.id, d.data()))
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
}

export async function createHost(
  institutionUid: string,
  input: CreateHostInput
): Promise<IHost> {
  await ensureCenterDoc(institutionUid);
  const payload = {
    name: input.name.trim(),
    kind: input.kind ?? "physical",
    ip: input.ip?.trim() || null,
    anydeskId: input.anydeskId?.trim() || null,
    anydeskPass: input.anydeskPass?.trim() || null,
    os: input.os?.trim() || null,
    osFamily: input.osFamily ?? null,
    osVersion: input.osVersion?.trim() || null,
    osUser: input.osUser?.trim() || null,
    osPass: input.osPass?.trim() || null,
    hostname: input.hostname?.trim() || null,
    hardware: input.hardware?.trim() || null,
    verifiedAt: input.verifiedAt?.trim() || null,
    verifiedBy: input.verifiedBy?.trim() || null,
    customFields: input.customFields ?? [],
  };
  const ref = await addDoc(hostsCollection(institutionUid), payload);
  return mapHost(ref.id, payload);
}

export async function updateHost(
  institutionUid: string,
  hostId: string,
  input: UpdateHostInput
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (input.name !== undefined) payload.name = input.name.trim();
  if (input.kind !== undefined) payload.kind = input.kind;
  if (input.ip !== undefined) payload.ip = input.ip.trim() || null;
  if (input.anydeskId !== undefined) payload.anydeskId = input.anydeskId.trim() || null;
  if (input.anydeskPass !== undefined)
    payload.anydeskPass = input.anydeskPass.trim() || null;
  if (input.os !== undefined) payload.os = input.os.trim() || null;
  if (input.osFamily !== undefined) payload.osFamily = input.osFamily || null;
  if (input.osVersion !== undefined)
    payload.osVersion = input.osVersion.trim() || null;
  if (input.osUser !== undefined) payload.osUser = input.osUser.trim() || null;
  if (input.osPass !== undefined) payload.osPass = input.osPass.trim() || null;
  if (input.hostname !== undefined) payload.hostname = input.hostname.trim() || null;
  if (input.hardware !== undefined) payload.hardware = input.hardware.trim() || null;
  if (input.verifiedAt !== undefined) payload.verifiedAt = input.verifiedAt.trim() || null;
  if (input.verifiedBy !== undefined) payload.verifiedBy = input.verifiedBy.trim() || null;
  if (input.customFields !== undefined) payload.customFields = input.customFields;

  await updateDoc(
    doc(db, CENTER_INFRA_COLLECTION, institutionUid, HOSTS_SUBCOLLECTION, hostId),
    payload
  );
}

export async function deleteHost(
  institutionUid: string,
  hostId: string
): Promise<void> {
  await deleteDoc(
    doc(db, CENTER_INFRA_COLLECTION, institutionUid, HOSTS_SUBCOLLECTION, hostId)
  );
}

export async function listSoftwareInstances(
  institutionUid: string
): Promise<ISoftwareInstance[]> {
  const snapshot = await getDocs(instancesCollection(institutionUid));
  return snapshot.docs.map((d) => mapInstance(d.id, d.data()));
}

export async function createSoftwareInstance(
  institutionUid: string,
  input: CreateSoftwareInstanceInput
): Promise<ISoftwareInstance> {
  await ensureCenterDoc(institutionUid);
  const payload = {
    softwareTypeId: input.softwareTypeId,
    hostId: input.hostId || null,
    active: input.active ?? true,
    mail: input.mail?.trim() || null,
    password: input.password?.trim() || null,
    url: input.url?.trim() || null,
    version: input.version?.trim() || null,
    lastUpdatedAt: input.lastUpdatedAt?.trim() || null,
    verifiedAt: input.verifiedAt?.trim() || null,
    verifiedBy: input.verifiedBy?.trim() || null,
    dicomEndpoints: input.dicomEndpoints ?? [],
    customFields: input.customFields ?? [],
  };
  const ref = await addDoc(instancesCollection(institutionUid), payload);
  return mapInstance(ref.id, payload);
}

export async function updateSoftwareInstance(
  institutionUid: string,
  instanceId: string,
  input: UpdateSoftwareInstanceInput
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (input.softwareTypeId !== undefined) payload.softwareTypeId = input.softwareTypeId;
  if (input.hostId !== undefined) payload.hostId = input.hostId || null;
  if (input.active !== undefined) payload.active = input.active;
  if (input.mail !== undefined) payload.mail = input.mail.trim() || null;
  if (input.password !== undefined) payload.password = input.password.trim() || null;
  if (input.url !== undefined) payload.url = input.url.trim() || null;
  if (input.version !== undefined) payload.version = input.version.trim() || null;
  if (input.lastUpdatedAt !== undefined) {
    payload.lastUpdatedAt = input.lastUpdatedAt.trim() || null;
  }
  if (input.verifiedAt !== undefined) {
    payload.verifiedAt = input.verifiedAt.trim() || null;
  }
  if (input.verifiedBy !== undefined) {
    payload.verifiedBy = input.verifiedBy.trim() || null;
  }
  if (input.dicomEndpoints !== undefined) payload.dicomEndpoints = input.dicomEndpoints;
  if (input.customFields !== undefined) payload.customFields = input.customFields;

  await updateDoc(
    doc(
      db,
      CENTER_INFRA_COLLECTION,
      institutionUid,
      SOFTWARE_INSTANCES_SUBCOLLECTION,
      instanceId
    ),
    payload
  );
}

export async function deleteSoftwareInstance(
  institutionUid: string,
  instanceId: string
): Promise<void> {
  await deleteDoc(
    doc(
      db,
      CENTER_INFRA_COLLECTION,
      institutionUid,
      SOFTWARE_INSTANCES_SUBCOLLECTION,
      instanceId
    )
  );
}

function resolveSoftwareType(
  softwareTypeId: string,
  softwareTypes: ISoftwareType[]
): ISoftwareType | undefined {
  return (
    softwareTypes.find((t) => t.id === softwareTypeId) ||
    softwareTypes.find((t) => t.key === softwareTypeId)
  );
}

function resolveSoftwareLabel(
  softwareTypeId: string,
  softwareTypes: ISoftwareType[]
): string {
  return resolveSoftwareType(softwareTypeId, softwareTypes)?.displayName ?? softwareTypeId;
}

function resolveTargetVersion(
  softwareTypeId: string,
  softwareTypes: ISoftwareType[],
  targets: Record<string, string>
): string | undefined {
  const type = resolveSoftwareType(softwareTypeId, softwareTypes);
  if (!type) return targets[softwareTypeId];
  return targets[type.key] ?? targets[type.id] ?? targets[softwareTypeId];
}

/**
 * Inventario cruzado de software de todos los centros con ficha de infra.
 */
export async function listSoftwareInventory(): Promise<ISoftwareInventoryRow[]> {
  const [centersSnap, softwareTypes, settings] = await Promise.all([
    getDocs(collection(db, CENTER_INFRA_COLLECTION)),
    listSoftwareTypes(),
    getInfraSettings(),
  ]);
  const targets = settings.softwareTargetVersions ?? {};

  const rowsNested = await Promise.all(
    centersSnap.docs.map(async (centerDoc) => {
      const institutionUid = centerDoc.id;
      const [instances, hosts, user] = await Promise.all([
        listSoftwareInstances(institutionUid),
        listHosts(institutionUid),
        getUserById(institutionUid).catch(() => null),
      ]);

      const hostById = new Map(hosts.map((h) => [h.id, h]));
      const institutionName = user
        ? getUserFullName(user)
        : institutionUid;
      const institutionEmail = user?.email;

      return instances.map((inst): ISoftwareInventoryRow => {
        const host = inst.hostId ? hostById.get(inst.hostId) : undefined;
        const type = resolveSoftwareType(inst.softwareTypeId, softwareTypes);
        return {
          institutionUid,
          institutionName,
          institutionEmail,
          instanceId: inst.id,
          softwareTypeId: inst.softwareTypeId,
          softwareKey: type?.key,
          softwareLabel: resolveSoftwareLabel(inst.softwareTypeId, softwareTypes),
          version: inst.version,
          targetVersion: resolveTargetVersion(
            inst.softwareTypeId,
            softwareTypes,
            targets
          ),
          lastUpdatedAt: inst.lastUpdatedAt,
          verifiedAt: inst.verifiedAt,
          verifiedBy: inst.verifiedBy,
          active: inst.active,
          hostId: inst.hostId,
          hostName: host?.name,
          hostIp: host?.ip,
          hostVerifiedAt: host?.verifiedAt,
          hostVerifiedBy: host?.verifiedBy,
          url: inst.url,
        };
      });
    })
  );

  return rowsNested.flat().sort((a, b) => {
    const byCenter = a.institutionName.localeCompare(b.institutionName, "es");
    if (byCenter !== 0) return byCenter;
    return a.softwareLabel.localeCompare(b.softwareLabel, "es");
  });
}

/**
 * Inventario de hosts para seguimiento de verificación periódica.
 */
export async function listHostVerificationInventory(): Promise<
  IHostVerificationRow[]
> {
  const [centersSnap, settings] = await Promise.all([
    getDocs(collection(db, CENTER_INFRA_COLLECTION)),
    getInfraSettings(),
  ]);
  const policy = settings.osSupportPolicy ?? DEFAULT_OS_SUPPORT_POLICY;

  const rowsNested = await Promise.all(
    centersSnap.docs.map(async (centerDoc) => {
      const institutionUid = centerDoc.id;
      const [hosts, user] = await Promise.all([
        listHosts(institutionUid),
        getUserById(institutionUid).catch(() => null),
      ]);
      const institutionName = user ? getUserFullName(user) : institutionUid;

      return hosts.map((host): IHostVerificationRow => {
        const osVersion = resolveHostOsVersion(host) ?? undefined;
        const osSupportStatus = evaluateOsSupport(osVersion, policy);
        return {
          institutionUid,
          institutionName,
          institutionEmail: user?.email,
          hostId: host.id,
          hostName: host.name,
          hostKind: host.kind,
          hostIp: host.ip,
          anydeskId: host.anydeskId,
          os: host.os,
          osFamily: host.osFamily,
          osVersion,
          osSupportStatus,
          acceptsSoftwareUpdates: acceptsSoftwareUpdates(osSupportStatus),
          recommendedOsVersion: policy.recommended,
          minSupportedOsVersion: policy.minSupported,
          verifiedAt: host.verifiedAt,
          verifiedBy: host.verifiedBy,
        };
      });
    })
  );

  return rowsNested.flat().sort((a, b) => {
    const byCenter = a.institutionName.localeCompare(b.institutionName, "es");
    if (byCenter !== 0) return byCenter;
    return a.hostName.localeCompare(b.hostName, "es");
  });
}

async function ensureCenterDoc(institutionUid: string): Promise<void> {
  const ref = centerRef(institutionUid);
  const snap = await getDoc(ref);
  if (snap.exists()) return;
  const now = new Date().toISOString();
  await setDoc(ref, {
    institutionUid,
    contacts: [],
    publicIps: [],
    customFields: [],
    createdAt: now,
    updatedAt: now,
  });
}
